import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AuthLayout, AuthAlert } from './AuthLayout';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useLocaleStore } from '@/stores/locale';
import { signInWithPassword } from './api';
import { authErrorMessage } from '@/lib/api-errors';
import { hasDirectoryClaim } from '@/lib/directory-claim';

function buildSchema(t: TFunction<'auth'>) {
  return z.object({
    email: z.string().trim().toLowerCase().email(t('validation.emailInvalid')),
    password: z.string().min(1, t('validation.passwordRequired')),
  });
}
type FormValues = { email: string; password: string };

export function LoginPage() {
  const { t } = useTranslation('auth');
  const locale = useLocaleStore((s) => s.locale);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const isInvite = next?.startsWith('/accept-invite') ?? false;
  const prefillEmail = params.get('email');
  const [authError, setAuthError] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => buildSchema(t), [locale]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: prefillEmail ? { email: prefillEmail } : undefined });

  async function onSubmit(values: FormValues) {
    setAuthError(null);
    try {
      await signInWithPassword(values.email, values.password);
      navigate(next || '/', { replace: true });
    } catch (err) {
      setAuthError(authErrorMessage(err, t('login.signInError')));
    }
  }

  return (
    <AuthLayout title={t('login.title')} subtitle={t('login.subtitle')}>
      {hasDirectoryClaim() ? <p className="mb-4 text-sm text-muted">{t('directoryClaim.notice')}</p> : null}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        {authError ? <AuthAlert>{authError}</AuthAlert> : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">{t('login.emailLabel')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            readOnly={!!prefillEmail}
            className={prefillEmail ? 'cursor-default text-muted' : undefined}
            {...register('email')}
          />
          <FieldError>{errors.email?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t('login.passwordLabel')}</Label>
            <Link to="/forgot-password" className="text-xs font-semibold text-accent-ink hover:underline">
              {t('login.forgotPassword')}
            </Link>
          </div>
          <Input id="password" type="password" autoComplete="current-password" {...register('password')} />
          <FieldError>{errors.password?.message}</FieldError>
        </div>
        <Button type="submit" loading={isSubmitting} className="mt-2 w-full">
          {t('login.submit')}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        {isInvite ? t('login.noAccountInvite') : t('login.noAccountTrial')}{' '}
        <Link to={next ? `/signup?next=${encodeURIComponent(next)}` : '/signup'} className="font-semibold text-accent-ink hover:underline">
          {isInvite ? t('login.createPassword') : t('login.startTrial')}
        </Link>
      </p>
    </AuthLayout>
  );
}
